export enum ContactRoleType {
  SENDER = 'SENDER',
  RECIPIENT = 'RECIPIENT',
}

export enum ContactDataType {
  PERSONAL = 'PERSONAL',
  BUSINESS = 'BUSINESS',
}

export type PersonalContact = {
  address_country: string;
  address_line: string;
  city: string;
  country_of_birth: string | null;
  date_of_birth: string;
  email: string | null;
  first_name: string | null;
  gender: string;
  id_number: string;
  id_type: string;
  industry_category: string | null;
  industry_sub_category: string | null;
  last_name: string;
  middle_name: string | null;
  mobile_number: string;
  mobile_number_prefix: string;
  nationality: string;
  occupation: string | null;
  other_name: string | null;
  postal: string;
  residential_status: string | null;
  state_or_province: string;
  bank_name?: string;
  bank_account_number?: string;
};

export type BusinessContact = {
  address_country: string;
  address_line: string;
  business_name: string;
  business_reg_number: string;
  city: string;
  country_of_incorporation: string;
  country_of_operation: string;
  date_of_incorporation: string;
  email: string | null;
  industry_category: string | null;
  industry_sub_category: string | null;
  mobile_number: string;
  mobile_number_prefix: string;
  state_or_province: string;
  postal: string;
  website: string | null;
  bank_name?: string;
  bank_account_number?: string;
};

export type BankContact = {
  bank_country: string;
  bank_account_currency: string;
  bank_account_number: string;
  bank_account_name: string;
  bank_name: string | null;
  bank_address: string | null;
  ts_bank_code: string | null;
  swift_bic_code: string | null;
  iban_code: string | null;
  cnaps_code: string | null;
};

export type FetchContactRes = {
  create_by: string;
  created: string;
  is_recipient: boolean;
  last_updated_by: string;
  last_updated: string;
  partner_contact_ref: string;
};

export type PostContactParams = {
  type: string;
  recipient: boolean;
  partner_contact_ref?: string;
  personal_contact?: PersonalContact;
  bank?: BankContact;
  business_contact?: BusinessContact;
  account_payable: boolean;
};

export type PostContactResponse = {
  partner_contact_ref: string;
};

export type PutContactParams = {
  type: string;
  personal_contact?: PersonalContact;
  bank?: BankContact;
  business_contact?: BusinessContact;
};

export type ContactData = (BusinessContact | PersonalContact) & FetchContactRes;

export type FetchAllContactResponse = {
  business_contact_list: (BusinessContact & FetchContactRes)[];
  personal_contact_list: (PersonalContact & FetchContactRes)[];
};

export type FetchContactResponse = {
  bank: BankContact;
  type: ContactDataType;
  business_contact: BusinessContact & FetchContactRes;
  personal_contact: PersonalContact & FetchContactRes;
};

export type PostQuotationPayload = {
  source_amount: number | null;
  destination_currency_code: string;
  destination_country_code: string;
  destination_amount: number | null;
};

export type PostTransferIDPayload = {
  tx_id: string;
  source_of_fund: string;
  purpose_of_transfer: string;
  recipient_id: string;
  sender_id: string;
};

export type PutQuotationPayload = {
  tx_id: string;
  source_amount: number;
  destination_amount: number;
  fx_rate: number;
  expiry_time: string;
  created_time: string;
  tx_disburse_i18n_id?: string;
};

export type GetHistoryTransaction = {
  product_name: string;
  domain: string;
  product_request_id: string;
  workflow_type: string;
};

export type GetActorData = {
  product_name: string;
  actor_name: string;
};

export type PostQuotationResponse = {
  tx_id: string;
  transfer_fee: number;
  converted_amount: number;
  fx_rate: number;
  source_amount: number;
  destination_amount: number;
  expiry_time: string;
  created_time: string;
};

export type SetContactPayload = {
  partner_contact_ref: string;
  tx_id: string;
  is_recipient: boolean;
};

export type SetInvoicePayload = {
  tx_id: string;
  url_list: string[];
};

export type FetchContactPayload = {
  isRecipient: boolean;
  isAccountPayable: boolean;
  bankCountry?: string;
  bankCurrency?: string;
};

export type InternationalTransaction = {
  attachmentUrls: string;
  createBy: string;
  created: string;
  destinationAmount: number;
  destinationCountry: string;
  destinationCurrency: string;
  errorMapping: string;
  expiryDate: number;
  fxRate: number;
  id: string;
  internalTxNote: string;
  lastUpdateBy: string;
  lastUpdated: string;
  purposeOfTransfer: string;
  recipientDetails: string;
  recipientId: string;
  recipientName: string;
  recordFlag: string;
  senderDetails: string;
  senderId: string;
  senderName: string;
  senderNote: string;
  sourceAmount: number;
  sourceOfFund: string;
  totalFee: number;
  txDate: number;
  txStatus: string;
  username: string;
  vendorFee: number;
};

export type GetTransactionResponse = {
  totalCount: number;
  limit: number;
  offset: number;
  transactions: InternationalTransaction[];
};

export type CreateTransferPayload = {
  source_of_fund: string;
  purpose_of_transfer: string;
  tx_id: string;
};

export type CancelTransferPayload = {
  tx_id: string;
};

export type CreateTransferResponse = {
  tx_id: string;
  tx_status: string;
};

export type RuleRequest = {
  product_name: string;
  domain: string;
  workflow_type: string;
  workflow_rules: Rule[];
  single_rule?: boolean;
};

export type ProductWorkflow = {
  owner: string;
  product_name: string;
  domain: string;
  workflow_type: string;
  workflow_rules: Rule[];
};

export type GetActor = {
  owner: string;
  product_name: string;
  domain: string;
  workflow_type: string;
  product_request_actor: ProductRequestActor[];
};

export type Rule = {
  id: string;
  level: number;
  permission_count_needed: number;
  rule: any;
  actors: Actor[];
  permission_type: string;
};

export type Actor = {
  actor_member: string;
  actor_type: string;
};

export type InternationalRemitApproval = {
  permissionCountNeeded: number;
  permissionCollected: number;
  permissionAllowed: number;
  permissionType: string;
  status: string;
  createBy: string;
  created: string;
  productRequestId: string;
  id: string;
  lastUpdateBy: string;
  lastUpdated: string;
  actorName: string;
  recordFlag: string;
};

export type ProductRequestActor = {
  product_request_id: string;
  actor: string;
  reason: string;
  actor_allowed: boolean;
};
